import { httpApi } from '@app/api/http.api'

export type NotificationSenderAddressRequest = {
  name: string
  description?: string
  server: string
  port: number
  password: string
  email: string
}

export type NotificationSenderAddressResponse = {
  id: string
  name: string
  description: string
  server: string
  port: number
  email: string
  isActive: boolean
}

export type GetAllSenderAddressesResponse = {
  id: string
  name: string
  email: string
  isActive: boolean
}

export type UpdateNotificationSenderAddressRequest = {
  id: string
  data: {
    currentPassword: string
    description: string
    email: string
    name: string
    newPassword: string
    password: string
    port: number
    server: string
  }
}

export type ToggleActivationRequest = {
  isActive: boolean
}

export type NotificationRequest = {
  notificationTemplateId: string
  notificationType: 'THRESHOLD_EXCEEDED' | 'ERROR' | 'FAILURE'
  companyId?: string
  deviceId: string
  firstName: string
  lastName: string
  email: string
  phoneCountryCode: string
  phoneNumber: string
  dataType: string
  dataIndex: number
  bitIndex?: number
  dataValue: number
  condition: string
  description: string
  quantityOption: string
  interval?: string
  language: string
  notificationMethods: ('EMAIL' | 'SMS')[]
  additionalNotificationRecipients: string[]
}

export type NotificationResponse = {
  id: string
  notificationTemplateId?: string
  notificationType: 'THRESHOLD_EXCEEDED' | 'ERROR' | 'FAILURE'
  companyId?: string
  company?: {
    id: string
    name: string
  }
  deviceId?: string
  device?: {
    id: string
    name: string
    serialNumber: string
  }
  firstName: string
  lastName: string
  email: string
  phoneCountryCode?: string
  phoneNumber?: string
  dataType: string
  dataIndex: number
  bitIndex?: number
  dataValue: number
  condition: string
  description: string
  quantityOption: string
  interval?: number
  notificationMethods: ('EMAIL' | 'SMS')[]
  additionalNotificationRecipients?: {
    id: string
    name: string
    email: string
  }[]
  status: 'ACTIVE' | 'INACTIVE'
  language: string
  lastTriggeredAt?: string
  lastTriggeredValue?: string
  triggerCount: number
  isActive: boolean
  isDeleted: boolean
  createdBy?: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  createdById?: string
  createdAt: string
  updatedAt: string
}

export type UpdateNotificationRequest = Partial<NotificationRequest>

export type NotificationTemplateRequest = {
  name: string
  description?: string
  subjectPL: string
  subjectEN: string
  contentPL: string
  contentEN: string
  templateType: string
  isActive?: boolean
}

export type NotificationTemplateResponse = {
  id: string
  name: string
  description?: string
  subjectPL: string
  subjectEN: string
  contentPL: string
  contentEN: string
  templateType: string
  isActive: boolean
  isDeleted: boolean
  createdAt: string
  updatedAt: string
}

export type NotificationLogResponse = {
  id: string
  senderAddressId: string
  recipientEmail: string
  subject: string
  status: 'sent' | 'failed' | 'pending'
  error?: string
  sentAt?: string
  createdAt: string
}

export type TestNotificationRequest = {
  senderAddressId: string
  toEmail: string
  subject: string
  content: string
}
export type GetSenderAddressDetailsResponse = {
  description: string
  port: number
  server: string
}

const NOTIFICATIONS = 'notifications'
const NOTIFICATION_SENDER_ADDRESSES = 'notification-sender-addresses'

export const getAllSenderAddresses = (): Promise<GetAllSenderAddressesResponse[]> =>
  httpApi
    .get<GetAllSenderAddressesResponse[]>(`${NOTIFICATION_SENDER_ADDRESSES}/all`)
    .then(({ data }) => data.map(({ id, name, email, isActive }) => ({ id, name, email, isActive })))

export const getSenderAddressDetails = (id: string): Promise<GetSenderAddressDetailsResponse> =>
  httpApi.get<GetSenderAddressDetailsResponse>(`${NOTIFICATION_SENDER_ADDRESSES}/${id}`).then(({ data }) => data)

export const createSenderAddress = (payload: NotificationSenderAddressRequest): Promise<string> =>
  httpApi.post<string>(`${NOTIFICATION_SENDER_ADDRESSES}`, payload).then(({ data }) => data)

export const updateSenderAddress = ({ id, data }: UpdateNotificationSenderAddressRequest): Promise<void> =>
  httpApi.put<void>(`${NOTIFICATION_SENDER_ADDRESSES}/${id}`, data).then(({ data }) => data)

export const deleteSenderAddress = (id: string): Promise<{ message: string }> =>
  httpApi.delete<{ message: string }>(`${NOTIFICATION_SENDER_ADDRESSES}/${id}`).then(({ data }) => data)

export const toggleSenderAddressActivation = (
  id: string,
  payload: ToggleActivationRequest,
): Promise<NotificationSenderAddressResponse> =>
  httpApi
    .patch<NotificationSenderAddressResponse>(`${NOTIFICATION_SENDER_ADDRESSES}/${id}/toggle-activation`, payload)
    .then(({ data }) => data)

export const getAllNotifications = (): Promise<NotificationResponse[]> =>
  httpApi.get<NotificationResponse[]>(`${NOTIFICATIONS}`).then(({ data }) => data)

export const createNotification = (payload: NotificationRequest): Promise<NotificationResponse> =>
  httpApi.post<NotificationResponse>(`${NOTIFICATIONS}`, payload).then(({ data }) => data)

export const updateNotification = (id: string, payload: UpdateNotificationRequest): Promise<NotificationResponse> =>
  httpApi.put<NotificationResponse>(`${NOTIFICATIONS}/${id}`, payload).then(({ data }) => data)

export const deleteNotification = (id: string): Promise<{ message: string }> =>
  httpApi.delete<{ message: string }>(`${NOTIFICATIONS}/${id}`).then(({ data }) => data)

export const toggleNotificationActive = (
  id: string,
  payload: { isActive: boolean; performedById?: string },
): Promise<NotificationResponse> =>
  httpApi.patch<NotificationResponse>(`${NOTIFICATIONS}/${id}/toggle-active`, payload).then(({ data }) => data)
